// VIS-267 step 3 (engine-generic): what moves on its own (herds, rides, tied horses, guests, karts) is posed only where
// it can be seen, and far small ones at a lower rate. Each system keeps its own logic (where an animal walks, where a
// sled is on its line, the give-way board, the race) at its full rate everywhere: it is cheap, and nothing is ever
// anywhere else than it would have been. The costly part, the pose (mixers, bones, matrices, ropes, instance uploads,
// the shadow keeper's live map that follows them), asks pose() first:
//  - out of view (its sphere, grown along its sun shadow, outside the camera's view): not posed, the time owed grows;
//  - in view and at least NEAR_PX tall on screen: posed every frame (smooth);
//  - in view and smaller: posed on the far tick only (`rate` a second; one tick for all, so every far mover changes on
//    the same pictures and the map draws only those: rest.js), with all the time owed since its last pose.
// A mover coming into view is posed at once with the whole time owed: its clip's phase is where it would have been.
// awake (rest.js lifeAwake: the map draws this picture anyway, a finger, a glide, the seconds after a touch, sway or a near
// mover keeping it awake): far movers are posed on it too, as smooth as the map (owner, 2026-10-26: far rides looked
// laggy); they still count as far, so they never keep the map awake themselves. Only a resting map (asleep, or its judge
// linking far poses) keeps them on the tick. full (a capture: Film, the Renders tab; the start's cover): everything in
// view is posed every frame. rate Infinity = no far tick: every mover in view every frame.
import * as THREE from 'three/webgpu';
import { capture } from './capture.js'; // a capture (Film, the Renders tab): every mover in view every frame

export const NEAR_PX = 20; // CSS px tall on screen from which a mover is near (posed every frame)
const SHADOW_RUN = 120; // m: the longest mover shadow looked for (shadows.js LIVE_SEEN)
const MARGIN = 0.05; // a mover's sphere grown by this share of its distance (seen)

/** the far movers' poses a second on the phone set (main.js; computers: every frame). The owner, 2026-10-26: "choose the
 *  amount that is the most optimal, that still looks good": 5 (a third of the phone's 15 pictures a second; 2.5 reads as
 *  a stutter on a walking animal's legs). Since the far movers pose on every picture while the map is awake this is the
 *  resting rate only; &preview=farpace-<n> (main.js FAR_PICK: 5 today, 7.5) sets it for the owner's pick */
export const FAR_RATE = 5;

export function createLifePace() {
  const F = new THREE.Frustum(), PM = new THREE.Matrix4(), S = new THREE.Sphere(), sun = new THREE.Vector3();
  let camera = null, canvas = null, rate = Infinity, T = 0, dt = 0, since = 0, tick = false, awake = false, full = true, all = false, perM = 0, run = 0, gx = 0, gz = 0;
  const st = { near: 0, far: 0, off: 0, posed: 0, ticks: 0, seq: 0 }; // seq: poses so far, never reset (shadows.js: a pose on a step that drew nothing still redraws the movers' map)
  const api = {
    /** main.js: the camera, the canvas (its CSS height) and the far rate (FAR_RATE on the phone set, else Infinity) */
    setup(o) { ({ camera = camera, canvas = canvas } = o); if (o.rate !== undefined) rate = o.rate; return api; },
    get rate() { return rate; },
    /** this frame's far tick (far movers are posed on it) */
    get tick() { return tick; },
    /** main.js, once per world step before the movers' updates. sunDir = towards the sun (null or below the horizon: no
     *  shadow on the ground to keep in view) */
    frame(step, { awake: a = false, full: f = false, sunDir = null } = {}) {
      dt = step; T += step; awake = a; full = f || !camera || capture.on; all = capture.on; // a capture may draw from another camera (the Renders tab): every mover posed, in view or not
      st.near = st.far = st.off = st.posed = 0;
      since += step;
      tick = rate === Infinity || since >= 1 / rate - 1 / 240; // (keeps its beat on awake frames too: the first resting pose comes on it)
      if (tick) { st.ticks++; since = rate === Infinity ? 0 : Math.min(since - 1 / rate, 1 / rate); }
      if (!camera) return;
      camera.updateMatrixWorld();
      F.setFromProjectionMatrix(PM.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse), camera.coordinateSystem, camera.reversedDepth);
      perM = (Math.max(1, canvas?.clientHeight || 1) * (camera.zoom || 1)) / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2));
      run = 0;
      if (sunDir && sunDir.y > 0.01) { sun.copy(sunDir).normalize(); const s = Math.max(0.05, sun.y), g = Math.hypot(sun.x, sun.z) || 1; run = Math.sqrt(Math.max(0, 1 - s * s)) / s; gx = -sun.x / g; gz = -sun.z / g; }
    },
    /** is a sphere (centre x y z, radius r) or the shadow of a thing h m tall standing in it in view? The sphere is grown
     *  by MARGIN of its distance (at least 2 m): the camera may still move a little this frame after the movers stepped
     *  (main.js: the ride view, the map's bounds) */
    seen(x, y, z, r, h = 2 * r) {
      if (!camera) return true;
      const k = Math.min(SHADOW_RUN, h * run) / 2, p = camera.position;
      S.center.set(x + gx * k, y, z + gz * k); S.radius = r + k + Math.max(2, MARGIN * Math.hypot(x - p.x, y - p.y, z - p.z));
      return F.intersectsSphere(S);
    },
    /** One mover: the seconds to pose it by now (0 = not this frame). m = the mover's own record (any object, kept by
     *  the caller; pose() keeps its clock and its last posed spot in m.lifeT, m.lifeAt). x y z r = its sphere where its
     *  logic has it now, h = its height (m). Out of view = where it is now AND where it was last drawn are both out of
     *  view (a camera turning back onto an old spot finds it gone from there, posed where it is). */
    pose(m, x, y, z, r = 1, h = 2 * r) {
      if (m.lifeT === undefined) { m.lifeT = T - dt; m.lifeOff = true; } // a new mover: its first look in view is an entry
      const at = m.lifeAt;
      if (!all && !api.seen(x, y, z, r, h) && !(at && api.seen(at[0], at[1], at[2], r, h))) { st.off++; m.lifeOff = true; return 0; }
      const owed = T - m.lifeT, entry = m.lifeOff === true;
      m.lifeOff = false;
      if (!full && !entry && rate !== Infinity && camera) {
        const d = Math.max(0.1, camera.position.distanceTo(S.center.set(x, y, z)));
        if ((h * perM) / d < NEAR_PX) { st.far++; if (!tick && !awake) return 0; } else st.near++; // (an awake frame poses it, still counted far: rest.js wakes for near ones only)
      } else st.near++;
      m.lifeT = T; st.posed++; st.seq++;
      if (at) { at[0] = x; at[1] = y; at[2] = z; } else m.lifeAt = [x, y, z];
      return owed;
    },
    /** a mover the caller found out of view or hidden itself (not asked this frame): its next pose in view is an entry */
    away(m) { if (m.lifeT === undefined) m.lifeT = T; m.lifeOff = true; },
    /** this frame's counts, read in place (rest.js, every frame): near / far / off = movers asked, by kind; posed =
     *  posed now (all in view); ticks = far ticks so far */
    counts: st,
    info: () => ({ ...st, rate, awake, full, tick }),
  };
  return api;
}

/** the map's one life pace (main.js sets it up; every life system asks it) */
export const lifePace = createLifePace();
